import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/nexus";
import { X, Loader2, Search, Radar, TestTube2, Bug, Cpu, ShieldAlert, Settings2, Copy, Check } from "lucide-react";

const META = {
  investigate:      { label: "Agentic Investigation", icon: Search,   color: "#00F0FF", verb: "Investigating" },
  research:         { label: "Agentic Research",      icon: Radar,    color: "#9D4EDD", verb: "Researching" },
  "test-transaction": { label: "Agentic Test Transaction", icon: TestTube2, color: "#FFB000", verb: "Simulating" },
  "pen-test":       { label: "Agentic Pen Test",      icon: Bug,      color: "#FF3366", verb: "Probing" },
};

const SEVERITY_COLORS = { info: "#00F0FF", watch: "#FFB000", critical: "#FF3366" };

export default function AgenticActionDialog({ actionKey, alertId, agentId, onClose }) {
  const meta = META[actionKey];
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("emergent-claude");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data.items));
  }, []);

  const run = async () => {
    setRunning(true); setResult(null); setError(null);
    try {
      const r = await api.post(`/actions/${actionKey}`, {
        alert_id: alertId, agent_id: agentId, model_id: modelId,
      });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="panel brackets max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        data-testid={`action-dialog-${actionKey}`}
      >
        <span className="b1" /><span className="b2" />

        {/* Header */}
        <div className="p-4 border-b border-nexus-border flex justify-between items-start gap-3">
          <div>
            <div className="flex items-center gap-2">
              <meta.icon size={16} style={{ color: meta.color }} />
              <div className="panel-title" style={{ color: meta.color }}>{meta.label}</div>
            </div>
            <h2 className="font-rajdhani font-bold text-xl mt-1 tracking-widest uppercase">
              {result ? "Findings Ready" : running ? `${meta.verb}…` : "Configure & Run"}
            </h2>
          </div>
          <button data-testid="close-action-dialog" onClick={onClose} className="text-nexus-text-3 hover:text-nexus-text">
            <X size={16} />
          </button>
        </div>

        {/* Model selector */}
        <div className="p-4 border-b border-nexus-border space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Cpu size={13} className="text-nexus-cyan" />
              <span className="panel-title">Model</span>
            </div>
            <Link to="/models" className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-2 hover:text-nexus-cyan flex items-center gap-1">
              <Settings2 size={11} /> Manage models
            </Link>
          </div>
          <select
            data-testid="action-model-select"
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            disabled={running}
            className="w-full bg-nexus-bg border border-nexus-border text-[12px] font-mono px-2 py-1.5 focus:border-nexus-cyan/60 outline-none disabled:opacity-50"
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} — {m.model_id}
                {m.builtin ? " (built-in)" : ""}
              </option>
            ))}
          </select>
          <div className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3">
            Custom models can correlate for vulnerability finding, intent analysis, or cross-domain OSINT.
          </div>
        </div>

        {/* Actions */}
        {!result && !running && (
          <div className="p-4 flex justify-end gap-2 border-b border-nexus-border">
            <button data-testid="run-action-btn" onClick={run} className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 border" style={{ borderColor: meta.color, color: meta.color }}>
              ▶ Run {meta.label.split(" ").pop()}
            </button>
          </div>
        )}

        {/* Running */}
        {running && (
          <div className="p-8 flex flex-col items-center gap-3 border-b border-nexus-border">
            <Loader2 className="animate-spin" size={22} style={{ color: meta.color }} />
            <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: meta.color }}>{meta.verb}…</div>
            <div className="text-[11px] text-nexus-text-2">Correlating agent schema · on-chain flows · physical fingerprint</div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4 border-b border-nexus-rose/40 bg-nexus-rose/5">
            <div className="flex items-start gap-2 text-nexus-rose">
              <ShieldAlert size={14} className="mt-0.5" />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest">Action failed</div>
                <div className="text-[12px] mt-1">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="flex-1 overflow-auto p-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-nexus-border p-2">
                <div className="panel-title">Model</div>
                <div className="text-[12px] font-mono mt-1 text-nexus-cyan">{result.model_used.label}</div>
              </div>
              <div className="border border-nexus-border p-2">
                <div className="panel-title">Duration</div>
                <div className="metric-hero text-lg mt-1">{(result.duration_ms / 1000).toFixed(1)}s</div>
              </div>
              <div className="border border-nexus-border p-2">
                <div className="panel-title">Risk Δ</div>
                <div className="metric-hero text-lg mt-1" style={{ color: result.risk_delta >= 15 ? "#FF3366" : result.risk_delta >= 5 ? "#FFB000" : result.risk_delta <= -5 ? "#00F0FF" : "#94A3B8" }}>
                  {result.risk_delta > 0 ? "+" : ""}{result.risk_delta}
                </div>
              </div>
            </div>

            {result.attribution && (
              <div className="border border-nexus-border p-3">
                <div className="panel-title mb-1">Attribution</div>
                <div className="text-[12px] text-nexus-text">{result.attribution}</div>
              </div>
            )}

            <div>
              <div className="panel-title mb-2">Findings</div>
              <div className="space-y-2">
                {(result.findings || []).map((f, i) => {
                  const c = SEVERITY_COLORS[f.severity] || "#00F0FF";
                  return (
                    <div key={`${f.step}:${(f.detail || "").slice(0, 24)}`} className="border-l-2 pl-3 pr-2 py-1" style={{ borderColor: c }} data-testid={`finding-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: c }}>{f.severity || "info"}</span>
                        <span className="font-rajdhani font-semibold uppercase tracking-widest text-[12px]">{f.step}</span>
                      </div>
                      <div className="text-[12px] text-nexus-text-2 mt-0.5">{f.detail}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {result.recommendation && (
              <div className="border border-nexus-cyan/40 bg-nexus-cyan/5 p-3">
                <div className="panel-title mb-1"><span className="dot" />Recommendation</div>
                <div className="text-[12px] text-nexus-text">{result.recommendation}</div>
              </div>
            )}

            <details className="border border-nexus-border/60">
              <summary className="cursor-pointer px-3 py-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-nexus-text-2 hover:text-nexus-text">
                <span>Raw model output</span>
                <button
                  data-testid="copy-raw-output"
                  onClick={(e) => { e.preventDefault(); navigator.clipboard.writeText(result.raw_output || ""); setCopied(true); setTimeout(() => setCopied(false), 1400); }}
                  className="text-nexus-cyan flex items-center gap-1"
                >
                  {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "copied" : "copy"}
                </button>
              </summary>
              <pre className="px-3 py-2 text-[11px] font-mono text-nexus-text-2 whitespace-pre-wrap max-h-40 overflow-auto">{result.raw_output}</pre>
            </details>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
